import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';

const severityClassMap = {
  low: 'badge-info',
  medium: 'badge-warning',
  high: 'badge-danger',
};

const statusClassMap = {
  unread: 'badge-warning',
  read: 'badge-info',
  dismissed: 'badge-info',
  resolved: 'badge-success',
};

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString('pt-BR') : 'Nao informado';
}

const PersonalFinanceNotificationsPage = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [runningJobs, setRunningJobs] = useState(false);
  const [notifications, setNotifications] = useState({ summary: {}, items: [] });
  const [jobs, setJobs] = useState([]);

  const loadData = async (refresh = false) => {
    try {
      setLoading(true);
      const [notificationsResponse, jobsResponse] = await Promise.all([
        axios.get('/api/personal-finance/notifications', {
          params: {
            refresh,
            limit: 12,
          },
        }),
        axios.get('/api/personal-finance/jobs', {
          params: {
            limit: 12,
          },
        }),
      ]);

      setNotifications(notificationsResponse.data || { summary: {}, items: [] });
      setJobs(Array.isArray(jobsResponse.data) ? jobsResponse.data : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar alertas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(true);
  }, []);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await axios.post('/api/personal-finance/notifications/refresh', {
        limit: 12,
      });
      toast.success('Alertas atualizados');
      await loadData(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao atualizar alertas');
    } finally {
      setRefreshing(false);
    }
  };

  const handleRunJobs = async () => {
    try {
      setRunningJobs(true);
      await axios.post('/api/personal-finance/jobs/run', {
        max_due_connections: 6,
      });
      toast.success('Jobs operacionais executados');
      await loadData(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao rodar jobs operacionais');
    } finally {
      setRunningJobs(false);
    }
  };

  const handleUpdateStatus = async (notificationId, status) => {
    try {
      await axios.post(`/api/personal-finance/notifications/${notificationId}/status`, { status });
      toast.success('Status atualizado');
      await loadData(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao atualizar status do alerta');
    }
  };

  const summary = notifications?.summary || {};
  const items = Array.isArray(notifications?.items) ? notifications.items : [];

  const cards = [
    {
      label: 'Nao lidos',
      value: String(summary.unread_count || 0),
      helper: 'Alertas que ainda nao entraram no seu loop de decisao',
    },
    {
      label: 'Abertos',
      value: String(summary.open_count || 0),
      helper: 'Sinais ainda ativos no inbox do modulo',
    },
    {
      label: 'Criticos',
      value: String(summary.high_open_count || 0),
      helper: 'Alertas de alta severidade pedindo acao curta',
    },
    {
      label: 'Conectividade',
      value: String(summary.connection_open_count || 0),
      helper: 'Consentimento, stale sync, reauth e backoff',
    },
    {
      label: 'Money loop',
      value: String(summary.money_loop_open_count || 0),
      helper: 'Caixa, faturas, revisao e insights prioritarios',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        {cards.map((item) => (
          <div key={item.label} className="card">
            <div className="card-body space-y-2">
              <p className="text-sm text-slate-500">{item.label}</p>
              <p className="text-2xl font-semibold text-slate-900">{loading ? '...' : item.value}</p>
              <p className="text-sm text-slate-500">{item.helper}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.08fr_0.92fr]">
        <div className="card">
          <div className="card-body space-y-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Alerts center</p>
                <h2 className="text-xl font-semibold text-slate-900">Inbox operacional do modulo pessoal</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                <button className="btn-secondary" onClick={() => loadData(false)} disabled={loading}>
                  Atualizar tela
                </button>
                <button className="btn-secondary" onClick={handleRefresh} disabled={refreshing}>
                  {refreshing ? 'Atualizando...' : 'Atualizar alertas'}
                </button>
                <button className="btn-primary" onClick={handleRunJobs} disabled={runningJobs}>
                  {runningJobs ? 'Rodando...' : 'Rodar jobs'}
                </button>
              </div>
            </div>

            <div className="rounded-3xl border border-emerald-100 bg-emerald-50/70 p-5">
              <p className="text-sm text-emerald-900">
                Este inbox nao replica um centro de notificacao genérico. Ele junta risco de conectividade e risco financeiro em uma fila curta de acao.
              </p>
              <p className="mt-2 text-sm text-emerald-800">
                Os jobs operacionais atualizam syncs devidos, reavaliam sinais do money loop e materializam alertas por usuario do household.
              </p>
            </div>

            <div className="space-y-3">
              {items.length === 0 && (
                <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-500">
                  Nenhum alerta aberto no momento. Se isso persistir com o modulo vazio, o proximo passo e conectar dados e revisar o caixa.
                </div>
              )}

              {items.map((item) => (
                <div key={item.id} className="rounded-3xl border border-slate-200 bg-white p-5">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={severityClassMap[item.severity] || 'badge-info'}>{item.severity}</span>
                        <span className={statusClassMap[item.status] || 'badge-info'}>{item.status}</span>
                        <span className="badge-info">{item.notification_type}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
                      <p className="text-sm text-slate-600">{item.body}</p>
                      <div className="flex flex-wrap gap-4 text-xs text-slate-400">
                        <span>Primeira vez: {formatDateTime(item.first_seen_at)}</span>
                        <span>Ultima vez: {formatDateTime(item.last_seen_at)}</span>
                        <span>Canal: {item.channel_scope}</span>
                        <span>Email: {item.email_status}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 lg:max-w-[240px] lg:justify-end">
                      {item.cta_route && (
                        <Link to={item.cta_route} className="btn-primary">
                          {item.cta_label || 'Abrir'}
                        </Link>
                      )}
                      {item.status !== 'read' && (
                        <button className="btn-secondary" onClick={() => handleUpdateStatus(item.id, 'read')}>
                          Marcar lido
                        </button>
                      )}
                      {item.status !== 'dismissed' && (
                        <button className="btn-secondary" onClick={() => handleUpdateStatus(item.id, 'dismissed')}>
                          Dispensar
                        </button>
                      )}
                      {item.status !== 'unread' && (
                        <button className="btn-secondary" onClick={() => handleUpdateStatus(item.id, 'unread')}>
                          Reabrir
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card">
            <div className="card-body space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Recent jobs</p>
                <h2 className="text-xl font-semibold text-slate-900">Execucao operacional</h2>
              </div>
              <div className="space-y-3">
                {jobs.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                    Nenhum job registrado ainda para este household.
                  </div>
                )}
                {jobs.map((job) => (
                  <div key={job.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-medium text-slate-900">{job.job_key}</p>
                        <p className="mt-1 text-sm text-slate-500">{job.trigger_type} / {formatDateTime(job.started_at)}</p>
                      </div>
                      <span className={job.status === 'success' ? 'badge-success' : job.status === 'error' ? 'badge-danger' : 'badge-warning'}>
                        {job.status}
                      </span>
                    </div>
                    {job.error_message && (
                      <p className="mt-3 text-sm text-rose-700">{job.error_message}</p>
                    )}
                    {job.summary_json && (
                      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {Object.entries(job.summary_json)
                          .filter(([, value]) => typeof value === 'number')
                          .slice(0, 6)
                          .map(([key, value]) => (
                            <div key={key} className="rounded-2xl border border-slate-200 bg-white p-3 text-sm">
                              <p className="text-slate-500">{key}</p>
                              <p className="mt-1 font-semibold text-slate-900">{String(value)}</p>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Como usar</p>
                <h2 className="text-xl font-semibold text-slate-900">Ordem recomendada</h2>
              </div>
              <div className="space-y-3 text-sm text-slate-600">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  Primeiro limpe alertas de conectividade. Sem dado confiavel, o forecast vira ruído.
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  Depois trate alertas de caixa e fatura. Eles mexem na decisao de curto prazo.
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  Por fim revise insights e feche a revisao semanal para registrar as acoes da proxima semana.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalFinanceNotificationsPage;
